import { getDefaultForRole, getModelsForProvider } from '@shared-models';

import type {
  AgentConfig,
  AgentToolName,
  LLMProvider,
  PersonalityPresetKey,
  PersonalitySliders,
  PersonalitySpec,
  PersonalityOutputLength,
  ToolPermission,
} from '../types/npc';

export interface PersonalityPresetDefinition {
  key: PersonalityPresetKey;
  label: string;
  description: string;
  sliders: PersonalitySliders;
}

export const DEFAULT_PERSONALITY_SLIDERS: PersonalitySliders = {
  formality: 0.5,
  warmth: 0.5,
  brevity: 0.5,
  creativity: 0.5,
  assertiveness: 0.5,
  skepticism: 0.5,
  humor: 0.5,
};

const preset = (
  key: PersonalityPresetKey,
  label: string,
  description: string,
  sliders: Partial<PersonalitySliders>,
): PersonalityPresetDefinition => ({
  key,
  label,
  description,
  sliders: {
    ...DEFAULT_PERSONALITY_SLIDERS,
    ...sliders,
  },
});

export const PERSONALITY_PRESETS: PersonalityPresetDefinition[] = [
  preset('neutral', 'Neutral', 'Balanced tone with no strong leanings.', {}),
  preset('analyst', 'Analyst', 'Structured and fact-driven communication.', {
    formality: 0.7,
    brevity: 0.6,
    skepticism: 0.6,
    creativity: 0.35,
  }),
  preset('coach', 'Coach', 'Encouraging and supportive mentor style.', {
    warmth: 0.85,
    assertiveness: 0.6,
    humor: 0.5,
    creativity: 0.6,
  }),
  preset('scholar', 'Scholar', 'Thorough and knowledgeable voice.', {
    formality: 0.8,
    brevity: 0.3,
    creativity: 0.4,
    skepticism: 0.5,
  }),
  preset('storyteller', 'Storyteller', 'Expressive and imaginative storyteller.', {
    creativity: 0.9,
    warmth: 0.8,
    brevity: 0.2,
    humor: 0.6,
  }),
  preset('critic', 'Critic', 'Direct and discerning analysis.', {
    skepticism: 0.85,
    assertiveness: 0.8,
    warmth: 0.25,
    formality: 0.6,
  }),
  preset('socratic', 'Socratic', 'Questioning and thought-provoking style.', {
    skepticism: 0.6,
    creativity: 0.6,
    assertiveness: 0.4,
    warmth: 0.5,
  }),
  preset('realist', 'Realist', 'Pragmatic and grounded voice.', {
    skepticism: 0.6,
    creativity: 0.3,
    assertiveness: 0.6,
    humor: 0.3,
  }),
  preset('optimist', 'Optimist', 'Warm and uplifting outlook.', {
    warmth: 0.8,
    humor: 0.6,
    creativity: 0.6,
    skepticism: 0.2,
  }),
];

export const MBTI_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'INTJ', label: 'Architect' },
  { code: 'INTP', label: 'Logician' },
  { code: 'ENTJ', label: 'Commander' },
  { code: 'ENTP', label: 'Debater' },
  { code: 'INFJ', label: 'Advocate' },
  { code: 'INFP', label: 'Mediator' },
  { code: 'ENFJ', label: 'Protagonist' },
  { code: 'ENFP', label: 'Campaigner' },
  { code: 'ISTJ', label: 'Logistician' },
  { code: 'ISFJ', label: 'Defender' },
  { code: 'ESTJ', label: 'Executive' },
  { code: 'ESFJ', label: 'Consul' },
  { code: 'ISTP', label: 'Virtuoso' },
  { code: 'ISFP', label: 'Adventurer' },
  { code: 'ESTP', label: 'Entrepreneur' },
  { code: 'ESFP', label: 'Entertainer' },
];

export const ENNEAGRAM_OPTIONS: Array<{ num: number; label: string }> = [
  { num: 1, label: 'The Reformer' },
  { num: 2, label: 'The Helper' },
  { num: 3, label: 'The Achiever' },
  { num: 4, label: 'The Individualist' },
  { num: 5, label: 'The Investigator' },
  { num: 6, label: 'The Loyalist' },
  { num: 7, label: 'The Enthusiast' },
  { num: 8, label: 'The Challenger' },
  { num: 9, label: 'The Peacemaker' },
];

export const OUTPUT_LENGTH_OPTIONS: PersonalityOutputLength[] = ['short', 'medium', 'long'];

const CHAT_MODEL_OPTIONS = (provider: LLMProvider): string[] => {
  const models = getModelsForProvider(provider)
    .filter((entry) => entry.capabilities.includes('chat'))
    .map((entry) => entry.id);
  if (models.length === 0) {
    console.warn(`No chat-capable models found for provider "${provider}"`);
  }
  return models;
};

export const LLM_MODEL_OPTIONS: Record<LLMProvider, string[]> = {
  gemini: CHAT_MODEL_OPTIONS('gemini'),
  openrouter: CHAT_MODEL_OPTIONS('openrouter'),
  anthropic: CHAT_MODEL_OPTIONS('anthropic'),
};
export const FREE_MODEL_SECTION_LABEL = 'Free Models';

const defaultNpcChatModel = getDefaultForRole('npc_chat');
const defaultAgentModel = getDefaultForRole('agent');

export const DEFAULT_LLM_PROVIDER: LLMProvider = defaultNpcChatModel.provider;
export const DEFAULT_LLM_MODEL = defaultNpcChatModel.modelId;
export const DEFAULT_NPC_MAX_TOKENS = 500;
export const DEFAULT_NPC_TEMPERATURE = 0.75;
export const DEFAULT_AGENT_MODEL = defaultAgentModel.modelId;
export const DEFAULT_AGENT_LOOP_ITERATIONS = 10;
export const DEFAULT_AGENT_MAX_TOKENS = 50_000;

export const AGENT_PERMISSION_OPTIONS: Array<{
  value: ToolPermission;
  label: string;
  description: string;
}> = [
  {
    value: 'read_memory',
    label: 'Read Memory',
    description: 'Recall structured memory entries.',
  },
  {
    value: 'write_memory',
    label: 'Write Memory',
    description: 'Store and update structured memories.',
  },
  {
    value: 'task_management',
    label: 'Task Updates',
    description: 'Mark agent task progress and completion.',
  },
  {
    value: 'web_access',
    label: 'Web Access',
    description: 'Search and fetch web content with rate limits.',
  },
  {
    value: 'skill_execution',
    label: 'Skill Execution',
    description: 'Load reusable workflow skills for multi-step tasks.',
  },
  {
    value: 'game_world',
    label: 'Game World',
    description: 'Trigger emotes and movement in the world.',
  },
];

export const AGENT_TOOL_PERMISSION_MAP: Record<AgentToolName, ToolPermission[]> = {
  memory_store: ['write_memory'],
  memory_recall: ['read_memory'],
  task_update: ['task_management'],
  game_emote: ['game_world'],
  game_navigate: ['game_world'],
  interact_with: ['game_world'],
  web_search: ['web_access'],
  web_fetch: ['web_access'],
  delegate_research: ['web_access'],
  skill_execute: ['skill_execution'],
};

export const AGENT_TOOL_LABELS: Record<AgentToolName, string> = {
  memory_store: 'memory_store',
  memory_recall: 'memory_recall',
  task_update: 'task_update',
  game_emote: 'game_emote',
  game_navigate: 'game_navigate',
  interact_with: 'interact_with',
  web_search: 'web_search',
  web_fetch: 'web_fetch',
  delegate_research: 'delegate_research',
  skill_execute: 'skill_execute',
};

export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  enabled: false,
  llmProvider: defaultAgentModel.provider === 'anthropic' ? 'anthropic' : 'openrouter',
  defaultModel: DEFAULT_AGENT_MODEL,
  permissions: ['read_memory', 'write_memory', 'task_management'],
  maxLoopIterations: DEFAULT_AGENT_LOOP_ITERATIONS,
  maxTokensPerTask: DEFAULT_AGENT_MAX_TOKENS,
  enabledTools: ['memory_store', 'memory_recall', 'task_update'],
  enabledSkills: [],
};

export const DEFAULT_NON_AGENT_ASSIGNED_TASK_PERMISSIONS: ToolPermission[] = [
  ...DEFAULT_AGENT_CONFIG.permissions,
  'game_world',
];
export const DEFAULT_NON_AGENT_ASSIGNED_TASK_TOOLS: AgentToolName[] = [
  ...DEFAULT_AGENT_CONFIG.enabledTools,
  'game_emote',
  'game_navigate',
  'interact_with',
];

const VALID_TOOL_PERMISSIONS: ToolPermission[] = AGENT_PERMISSION_OPTIONS.map(
  (option) => option.value,
);
const VALID_AGENT_TOOLS: AgentToolName[] = Object.keys(AGENT_TOOL_LABELS) as AgentToolName[];

const normalizePermissionList = (permissions: ToolPermission[] | undefined): ToolPermission[] => {
  const normalized = Array.isArray(permissions) ? permissions : [];
  return [
    ...new Set(normalized.filter((permission) => VALID_TOOL_PERMISSIONS.includes(permission))),
  ];
};

const normalizeToolList = (tools: AgentToolName[] | undefined): AgentToolName[] => {
  const normalized = Array.isArray(tools) ? tools : [];
  return [...new Set(normalized.filter((tool) => VALID_AGENT_TOOLS.includes(tool)))];
};

const ensureDelegateResearchTool = (
  tools: AgentToolName[],
  permissions: ToolPermission[],
): AgentToolName[] => {
  if (!permissions.includes('web_access')) {
    return tools;
  }
  if (!tools.includes('web_search') && !tools.includes('web_fetch')) {
    return tools;
  }
  return tools.includes('delegate_research') ? tools : [...tools, 'delegate_research'];
};

export const resolveNpcBackgroundTaskCapabilities = (npc: {
  agentConfig?: AgentConfig | null;
}): {
  agentEnabled: boolean;
  permissions: ToolPermission[];
  enabledTools: AgentToolName[];
} => {
  if (!npc.agentConfig) {
    return {
      agentEnabled: false,
      permissions: [...DEFAULT_NON_AGENT_ASSIGNED_TASK_PERMISSIONS],
      enabledTools: [...DEFAULT_NON_AGENT_ASSIGNED_TASK_TOOLS],
    };
  }

  const permissions = normalizePermissionList(npc.agentConfig.permissions);
  const enabledTools = ensureDelegateResearchTool(
    normalizeToolList(npc.agentConfig.enabledTools),
    permissions,
  );
  if (npc.agentConfig.enabled) {
    return {
      agentEnabled: true,
      permissions,
      enabledTools,
    };
  }

  return {
    agentEnabled: false,
    permissions: permissions.length > 0 ? permissions : [...DEFAULT_AGENT_CONFIG.permissions],
    enabledTools: enabledTools.length > 0 ? enabledTools : [...DEFAULT_AGENT_CONFIG.enabledTools],
  };
};

export const DEFAULT_PERSONALITY_SPEC: PersonalitySpec = {
  preset: 'neutral',
  sliders: { ...DEFAULT_PERSONALITY_SLIDERS },
  output: { length: 'medium' },
  mbti: null,
  enneagram: null,
};

export const NPC_CREATION_EXAMPLES = [
  'Socrates',
  'Aristotle',
  'A mysterious fortune teller who speaks in riddles',
  "A cheerful innkeeper who knows everyone's secrets",
  'A retired knight turned florist',
  'A nervous apprentice wizard prone to mishaps',
  'A traveling bard who only tells true stories',
];

export const findPersonalityPreset = (key: PersonalityPresetKey): PersonalityPresetDefinition =>
  PERSONALITY_PRESETS.find((entry) => entry.key === key) ?? PERSONALITY_PRESETS[0];

export const slidersForPreset = (key: PersonalityPresetKey): PersonalitySliders => {
  const selection = findPersonalityPreset(key);
  return { ...selection.sliders };
};

export const clampSliderValue = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0.5;
  }
  return Math.max(0, Math.min(1, value));
};

export const normalizeSliderValues = (sliders: PersonalitySliders): PersonalitySliders => ({
  formality: clampSliderValue(sliders.formality),
  warmth: clampSliderValue(sliders.warmth),
  brevity: clampSliderValue(sliders.brevity),
  creativity: clampSliderValue(sliders.creativity),
  assertiveness: clampSliderValue(sliders.assertiveness),
  skepticism: clampSliderValue(sliders.skepticism),
  humor: clampSliderValue(sliders.humor),
});

export const personalitySpecFromPreset = (key: PersonalityPresetKey): PersonalitySpec => ({
  preset: key,
  sliders: slidersForPreset(key),
  output: { length: 'medium' },
  mbti: null,
  enneagram: null,
});
